import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminStories() {
    const { getAuthHeaders } = useAuth();
    const [stories, setStories] = useState([]);
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingStory, setEditingStory] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        author_id: '',
        cover_image_url: '',
        full_html: '',
        preview_text: '',
        status: 'draft',
        exclusive: true
    });

    const fetchData = async () => {
        try {
            const [storiesRes, authorsRes] = await Promise.all([
                axios.get(`${API}/admin/stories`, { headers: getAuthHeaders() }),
                axios.get(`${API}/admin/authors`, { headers: getAuthHeaders() })
            ]);
            setStories(storiesRes.data);
            setAuthors(authorsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Nepodařilo se načíst data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [getAuthHeaders]);

    const openCreateDialog = () => {
        setEditingStory(null);
        setFormData({
            title: '',
            author_id: authors[0]?.id || '',
            cover_image_url: '',
            full_html: '',
            preview_text: '',
            status: 'draft',
            exclusive: true
        });
        setDialogOpen(true);
    };

    const openEditDialog = (story) => {
        setEditingStory(story);
        setFormData({
            title: story.title,
            author_id: story.author_id,
            cover_image_url: story.cover_image_url || '',
            full_html: story.full_html,
            preview_text: story.preview_text || '',
            status: story.status,
            exclusive: story.exclusive
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingStory) {
                await axios.put(`${API}/admin/stories/${editingStory.id}`, formData, {
                    headers: getAuthHeaders()
                });
                toast.success('Povídka byla upravena');
            } else {
                await axios.post(`${API}/admin/stories`, formData, {
                    headers: getAuthHeaders()
                });
                toast.success('Povídka byla vytvořena');
            }
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Nepodařilo se uložit povídku');
        }
    };

    const handleDelete = async (storyId) => {
        if (!confirm('Opravdu chcete smazat tuto povídku?')) return;
        
        try {
            await axios.delete(`${API}/admin/stories/${storyId}`, {
                headers: getAuthHeaders()
            });
            toast.success('Povídka byla smazána');
            fetchData();
        } catch (error) {
            toast.error('Nepodařilo se smazat povídku');
        }
    };

    return (
        <div data-testid="admin-stories">
            <div className="flex items-center justify-between mb-8">
                <h1 className="font-heading text-2xl md:text-3xl font-semibold">
                    Správa povídek
                </h1>
                <Button onClick={openCreateDialog} className="listarna-btn-primary" data-testid="create-story-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nová povídka
                </Button>
            </div>

            {/* Stories Table */}
            <div className="listarna-card rounded-lg overflow-hidden">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Název</th>
                            <th>Autor</th>
                            <th>Stav</th>
                            <th>Vytvořeno</th>
                            <th className="text-right">Akce</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="text-center py-8">
                                    <div className="animate-pulse">Načítání...</div>
                                </td>
                            </tr>
                        ) : stories.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="text-center py-8 text-muted-foreground">
                                    Žádné povídky
                                </td>
                            </tr>
                        ) : (
                            stories.map((story) => (
                                <tr key={story.id} data-testid={`story-row-${story.id}`}>
                                    <td className="font-medium">{story.title}</td>
                                    <td className="text-muted-foreground">{story.author_name}</td>
                                    <td>
                                        <span className={`px-2 py-1 rounded-full text-xs ${
                                            story.status === 'published' 
                                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                        }`}>
                                            {story.status === 'published' ? 'Publikováno' : 'Koncept'}
                                        </span>
                                    </td>
                                    <td className="text-muted-foreground">
                                        {new Date(story.created_at).toLocaleDateString('cs-CZ')}
                                    </td>
                                    <td className="text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <a 
                                                href={`/povidka/${story.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 hover:bg-secondary rounded-lg"
                                                title="Zobrazit"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </a>
                                            <button
                                                onClick={() => openEditDialog(story)}
                                                className="p-2 hover:bg-secondary rounded-lg"
                                                title="Upravit"
                                                data-testid={`edit-story-${story.id}`}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(story.id)}
                                                className="p-2 hover:bg-destructive/10 hover:text-destructive rounded-lg"
                                                title="Smazat"
                                                data-testid={`delete-story-${story.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {editingStory ? 'Upravit povídku' : 'Nová povídka'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                        <div>
                            <Label htmlFor="title">Název</Label>
                            <Input
                                id="title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                                data-testid="story-title-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="author">Autor</Label>
                            <Select 
                                value={formData.author_id}
                                onValueChange={(value) => setFormData({ ...formData, author_id: value })}
                            >
                                <SelectTrigger data-testid="story-author-select">
                                    <SelectValue placeholder="Vyberte autora" />
                                </SelectTrigger>
                                <SelectContent>
                                    {authors.map((author) => (
                                        <SelectItem key={author.id} value={author.id}>
                                            {author.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label htmlFor="cover_image_url">URL obrázku</Label>
                            <Input
                                id="cover_image_url"
                                value={formData.cover_image_url}
                                onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                                placeholder="https://..."
                                data-testid="story-cover-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="full_html">Text povídky (HTML)</Label>
                            <Textarea
                                id="full_html"
                                value={formData.full_html}
                                onChange={(e) => setFormData({ ...formData, full_html: e.target.value })}
                                rows={10}
                                required
                                placeholder="<p>Váš text...</p>"
                                data-testid="story-content-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Použijte HTML tagy: &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;blockquote&gt;
                            </p>
                        </div>

                        <div>
                            <Label htmlFor="preview_text">Náhled (volitelné)</Label>
                            <Textarea
                                id="preview_text"
                                value={formData.preview_text}
                                onChange={(e) => setFormData({ ...formData, preview_text: e.target.value })}
                                rows={3}
                                placeholder="Automaticky extrahováno z textu"
                                data-testid="story-preview-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="status">Stav</Label>
                            <Select 
                                value={formData.status}
                                onValueChange={(value) => setFormData({ ...formData, status: value })}
                            >
                                <SelectTrigger data-testid="story-status-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="draft">Koncept</SelectItem>
                                    <SelectItem value="published">Publikováno</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex justify-end gap-4 pt-4">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Zrušit
                            </Button>
                            <Button type="submit" className="listarna-btn-primary" data-testid="story-submit-btn">
                                {editingStory ? 'Uložit změny' : 'Vytvořit'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
